import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import PrintPageButton from "@/components/PrintPageButton";
import RowActions from "@/components/RowActions";
import DeductionOptimiser from "@/components/DeductionOptimiser";
import { FileText, AlertTriangle, CheckCircle2, Clock, TrendingUp, Receipt, IndianRupee, PlusCircle, Loader2, Sparkles } from "lucide-react";

/**
 * VAULT Tax Centre — India-focused MVP.
 *
 * Five tabs:
 *   1. Overview   — dashboard + section utilisation + next advance-tax due
 *   2. Deductions — CRUD for 80C / 80D / 80CCD(1B) / 24(b) / HRA / LTA / other
 *   3. Advance Tax — quarterly schedule, pay-now dialog, list of challans
 *   4. Capital Gains — auto-derived from Investments module
 *   5. Year-end Pack — one printable page combining everything
 *
 * Every list-view respects the platform-wide `RowActions` pattern for Edit/Delete.
 */
export default function TaxCentre() {
    const [catalogue, setCatalogue] = useState(null);
    const [fy, setFy] = useState("");
    const [availableFys, setAvailableFys] = useState([]);
    const [estimatedTax, setEstimatedTax] = useState(0);
    const [dashboard, setDashboard] = useState(null);
    const [tab, setTab] = useState("overview");

    // Load catalogue once
    useEffect(() => {
        api.get("/tax/catalogue").then((r) => {
            setCatalogue(r.data);
            setFy(r.data.current_fy);
            // Offer current FY + 3 previous FYs
            const cur = parseInt(r.data.current_fy.split("-")[0], 10);
            const fys = [];
            for (let i = 0; i < 4; i++) {
                const y = cur - i;
                fys.push(`${y}-${String(y + 1).slice(-2)}`);
            }
            setAvailableFys(fys);
        });
    }, []);

    // Load dashboard whenever FY or estimatedTax changes
    const reloadDashboard = () => {
        if (!fy) return;
        api.get("/tax/dashboard", { params: { fy, estimated_tax: estimatedTax } })
            .then((r) => setDashboard(r.data))
            .catch(() => setDashboard(null));
    };
    useEffect(reloadDashboard, [fy, estimatedTax]);

    if (!catalogue) {
        return <div className="p-10 flex items-center gap-2 text-stone-500"><Loader2 size={16} className="animate-spin" /> Loading Tax Centre…</div>;
    }

    return (
        <div className="space-y-6" data-testid="tax-centre">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="eyebrow">Tax Centre · India · FY {fy}</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">
                        Your year-round tax command centre.
                    </h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                        Track Section 80C / 80D deductions, quarterly advance-tax dues, and capital gains — all in one place.
                        Everything you file at year-end lives here.
                    </p>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <Label className="eyebrow">Financial Year</Label>
                        <Select value={fy} onValueChange={setFy}>
                            <SelectTrigger className="w-32 mt-1" data-testid="tax-fy-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {availableFys.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="eyebrow" title="Your own estimate of total FY tax — powers advance-tax targets">
                            Est. FY tax
                        </Label>
                        <Input
                            type="number"
                            className="w-36 mt-1 tabular-nums"
                            value={estimatedTax}
                            onChange={(e) => setEstimatedTax(parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            data-testid="tax-estimated-input"
                        />
                    </div>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="tax-tabs">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="deductions">Deductions</TabsTrigger>
                    <TabsTrigger value="advance">Advance Tax</TabsTrigger>
                    <TabsTrigger value="capital">Capital Gains</TabsTrigger>
                    <TabsTrigger value="pack">Year-end Pack</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-4">
                    <OverviewTab dashboard={dashboard} onJump={setTab} fy={fy} onOptimiserAccepted={reloadDashboard} />
                </TabsContent>
                <TabsContent value="deductions" className="pt-4">
                    <DeductionsTab fy={fy} catalogue={catalogue} onChange={reloadDashboard} />
                </TabsContent>
                <TabsContent value="advance" className="pt-4">
                    <AdvanceTaxTab fy={fy} estimatedTax={estimatedTax} onChange={reloadDashboard} />
                </TabsContent>
                <TabsContent value="capital" className="pt-4">
                    <CapitalGainsTab fy={fy} />
                </TabsContent>
                <TabsContent value="pack" className="pt-4">
                    <YearEndPackTab fy={fy} estimatedTax={estimatedTax} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/* ================================================================ */
/* OVERVIEW                                                          */
/* ================================================================ */
function OverviewTab({ dashboard, onJump, fy, onOptimiserAccepted }) {
    if (!dashboard) return <div className="text-stone-500 flex items-center gap-2 text-sm p-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
    const nextDue = dashboard.advance_tax_next_due;

    return (
        <div className="space-y-6" data-testid="tax-overview">
            {/* Optimiser callout */}
            <div className="flex items-start gap-3 p-4 border border-stone-200 rounded-md bg-gradient-to-br from-stone-50 to-white">
                <div className="flex-1">
                    <div className="eyebrow flex items-center gap-1"><Sparkles size={12} className="text-amber-500" /> Deduction Optimiser</div>
                    <div className="text-sm font-medium mt-1">Skip the manual entry — let VAULT scan your Investments & Expenses.</div>
                    <div className="text-xs text-stone-500 mt-1">
                        We match PPF/EPF/NPS/ELSS investments and health-insurance/loan/rent expenses to Section 80C/80D/24(b)/80E/HRA and pre-fill proofs in one click.
                    </div>
                </div>
                <DeductionOptimiser fy={fy} onAccepted={onOptimiserAccepted} />
            </div>

            {/* Top-line KPI cards */}
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
                <Kpi
                    label="Deductions claimed"
                    value={fmtMoney(dashboard.total_deductions_claimed)}
                    sub={dashboard.total_deduction_limit
                        ? `${dashboard.overall_deduction_utilisation_pct}% of limit`
                        : "No limit"}
                    testid="tax-kpi-deductions"
                />
                <Kpi
                    label="Advance tax paid"
                    value={fmtMoney(dashboard.advance_tax_total_paid)}
                    sub={nextDue
                        ? (nextDue.days_to_due >= 0
                            ? `Next: ${nextDue.quarter} in ${nextDue.days_to_due} days`
                            : `${nextDue.quarter} overdue`)
                        : "All quarters passed"}
                    tone={nextDue?.is_overdue ? "amber" : "stone"}
                    testid="tax-kpi-advance"
                />
                <Kpi
                    label="STCG"
                    value={fmtMoney(dashboard.stcg_total)}
                    sub="Short-term gains this FY"
                    testid="tax-kpi-stcg"
                />
                <Kpi
                    label="LTCG"
                    value={fmtMoney(dashboard.ltcg_total)}
                    sub="Long-term gains this FY"
                    testid="tax-kpi-ltcg"
                />
            </div>

            {/* Deduction utilisation strip */}
            <div className="border border-stone-200 rounded-md">
                <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                    <div className="eyebrow">Section utilisation</div>
                    <button onClick={() => onJump("deductions")} className="text-xs text-stone-500 hover:text-stone-900" data-testid="tax-jump-deductions">
                        Manage deductions →
                    </button>
                </div>
                <div className="p-3 space-y-2">
                    {dashboard.sections_summary.length === 0 && (
                        <div className="text-sm text-stone-500 text-center py-6">
                            No deductions claimed yet for this FY. Add your first proof under the <em>Deductions</em> tab.
                        </div>
                    )}
                    {dashboard.sections_summary.map((s) => (
                        <UtilRow key={s.section} section={s} />
                    ))}
                </div>
            </div>

            {/* Next-due callout */}
            {nextDue && nextDue.days_to_due >= 0 && (
                <div className="bg-stone-50 border border-stone-200 rounded-md p-4 flex items-start gap-3" data-testid="tax-next-due">
                    <Clock size={18} className="text-stone-500 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-medium">
                            Advance Tax {nextDue.quarter} — due {fmtDate(nextDue.due_date)}
                        </div>
                        <div className="text-xs text-stone-500 mt-1">
                            Target cumulative: <span className="font-mono-figure text-stone-800">{fmtMoney(nextDue.target_cumulative)}</span>
                            {" "}· Paid so far: <span className="font-mono-figure text-stone-800">{fmtMoney(nextDue.paid_cumulative)}</span>
                            {" "}· Shortfall: <span className="font-mono-figure text-red-700">{fmtMoney(nextDue.shortfall)}</span>
                        </div>
                    </div>
                    <button onClick={() => onJump("advance")} className="text-xs text-stone-700 hover:text-stone-900 underline">
                        Record payment →
                    </button>
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, sub, tone = "stone", testid }) {
    const toneCls = tone === "amber" ? "border-amber-200 bg-amber-50" : "border-stone-200 bg-white";
    return (
        <div className={`rounded-md border ${toneCls} p-3`} data-testid={testid}>
            <div className="eyebrow">{label}</div>
            <div className="text-2xl font-medium tabular-nums mt-1">{value}</div>
            {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

function UtilRow({ section }) {
    const pct = Math.min(100, section.utilisation_pct || 0);
    const tone = pct >= 90 ? "bg-emerald-600" : pct >= 50 ? "bg-blue-500" : "bg-stone-400";
    return (
        <div>
            <div className="flex items-center justify-between text-xs">
                <span className="text-stone-700 font-medium">{section.section} — {section.label}</span>
                <span className="tabular-nums text-stone-500">
                    {fmtMoney(section.total)} {section.limit && <>/ {fmtMoney(section.limit)}</>}
                </span>
            </div>
            {section.limit && (
                <div className="h-1.5 bg-stone-100 rounded mt-1 overflow-hidden">
                    <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

/* ================================================================ */
/* DEDUCTIONS                                                        */
/* ================================================================ */
function DeductionsTab({ fy, catalogue, onChange }) {
    const [summary, setSummary] = useState(null);
    const [openDlg, setOpenDlg] = useState(false);
    const [editing, setEditing] = useState(null);

    const reload = () => {
        api.get("/tax/deduction-summary", { params: { fy } })
            .then((r) => setSummary(r.data));
    };
    useEffect(reload, [fy]);

    const handleSave = async (form) => {
        try {
            if (editing?.id) {
                await api.patch(`/tax/proofs/${editing.id}`, form);
                toast.success("Deduction updated");
            } else {
                await api.post("/tax/proofs", { ...form, financial_year: fy });
                toast.success("Deduction added");
            }
            setOpenDlg(false);
            setEditing(null);
            reload();
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this deduction proof?")) return;
        try {
            await api.delete(`/tax/proofs/${id}`);
            toast.success("Deleted");
            reload();
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    if (!summary) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

    return (
        <div className="space-y-4" data-testid="tax-deductions">
            <div className="flex items-center justify-between">
                <div className="text-sm text-stone-600">
                    Total claimed: <span className="font-mono-figure font-medium text-stone-900">{fmtMoney(summary.total_claimed)}</span>
                </div>
                <div className="flex items-center gap-2">
                    <DeductionOptimiser fy={fy} onAccepted={() => { reload(); onChange?.(); }} testid="tax-optimiser-deductions" />
                    <Button onClick={() => { setEditing(null); setOpenDlg(true); }} data-testid="tax-add-deduction">
                        <PlusCircle size={14} className="mr-1" /> Add deduction
                    </Button>
                </div>
            </div>

            {summary.sections.map((sec) => (
                sec.total > 0 || sec.limit ? (
                    <SectionCard
                        key={sec.section}
                        section={sec}
                        onEdit={(p) => { setEditing(p); setOpenDlg(true); }}
                        onDelete={handleDelete}
                    />
                ) : null
            ))}

            <Dialog open={openDlg} onOpenChange={setOpenDlg}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit deduction" : "Add deduction"}</DialogTitle>
                        <DialogDescription>Record a Section 80C / 80D / HRA / LTA / other deduction proof for this FY.</DialogDescription>
                    </DialogHeader>
                    <DeductionForm
                        catalogue={catalogue}
                        initial={editing}
                        onSave={handleSave}
                        onCancel={() => { setOpenDlg(false); setEditing(null); }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SectionCard({ section, onEdit, onDelete }) {
    const pct = Math.min(100, section.utilisation_pct || 0);
    return (
        <div className="border border-stone-200 rounded-md" data-testid={`tax-section-${section.section}`}>
            <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                <div>
                    <div className="font-medium text-sm">{section.section} — {section.label}</div>
                    <div className="text-xs text-stone-500 mt-0.5">
                        {section.count} proof{section.count === 1 ? "" : "s"} · Total {fmtMoney(section.total)}
                        {section.limit && <> · Headroom {fmtMoney(section.headroom)} of {fmtMoney(section.limit)}</>}
                    </div>
                </div>
                <div className="text-right">
                    {section.limit && (
                        <>
                            <div className="text-xs text-stone-500">{pct}% used</div>
                            <div className="w-32 h-1.5 bg-stone-200 rounded mt-1 overflow-hidden">
                                <div className={`h-full ${pct >= 90 ? "bg-emerald-600" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                        </>
                    )}
                </div>
            </div>
            {section.proofs.length > 0 && (
                <table className="dense-table w-full">
                    <thead><tr><th>Date</th><th>Sub-category</th><th>Description</th><th className="text-right">Amount</th><th></th></tr></thead>
                    <tbody>
                        {section.proofs.map((p) => (
                            <tr key={p.id} data-testid={`tax-proof-row-${p.id}`}>
                                <td className="tabular-nums">{fmtDate(p.date)}</td>
                                <td>{p.sub_category || "—"}</td>
                                <td className="text-stone-500 text-xs">{p.description || "—"}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(p.amount)}</td>
                                <td>
                                    <RowActions
                                        onEdit={() => onEdit(p)}
                                        onDelete={() => onDelete(p.id)}
                                        testIdBase="tax-proof"
                                        id={p.id}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function DeductionForm({ catalogue, initial, onSave, onCancel }) {
    const [section, setSection] = useState(initial?.section || "80C");
    const [subCategory, setSubCategory] = useState(initial?.sub_category || "");
    const [amount, setAmount] = useState(initial?.amount || "");
    const [txDate, setTxDate] = useState(initial?.date || new Date().toISOString().slice(0, 10));
    const [description, setDescription] = useState(initial?.description || "");
    const [notes, setNotes] = useState(initial?.notes || "");
    const subCategories = catalogue.sections[section]?.sub_categories || [];

    const submit = () => {
        if (!section || !amount || !txDate) {
            toast.error("Section, amount, and date are required");
            return;
        }
        onSave({
            section,
            sub_category: subCategory || null,
            amount: parseFloat(amount),
            date: txDate,
            description: description || null,
            notes: notes || null,
        });
    };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Section</Label>
                    <Select value={section} onValueChange={(v) => { setSection(v); setSubCategory(""); }}>
                        <SelectTrigger className="mt-1" data-testid="tax-form-section"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {Object.entries(catalogue.sections).map(([key, sec]) => (
                                <SelectItem key={key} value={key}>{sec.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Sub-category</Label>
                    <Select value={subCategory} onValueChange={setSubCategory}>
                        <SelectTrigger className="mt-1" data-testid="tax-form-sub"><SelectValue placeholder="Optional" /></SelectTrigger>
                        <SelectContent>
                            {subCategories.map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 tabular-nums" data-testid="tax-form-amount" />
                </div>
                <div>
                    <Label>Date</Label>
                    <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} className="mt-1 tabular-nums" data-testid="tax-form-date" />
                </div>
            </div>
            <div>
                <Label>Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="LIC Jeevan Anand policy no. 12345" className="mt-1" data-testid="tax-form-desc" />
            </div>
            <div>
                <Label>Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 min-h-[60px]" data-testid="tax-form-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={submit} data-testid="tax-form-save">Save</Button>
            </div>
        </div>
    );
}

/* ================================================================ */
/* ADVANCE TAX                                                       */
/* ================================================================ */
function AdvanceTaxTab({ fy, estimatedTax, onChange }) {
    const [data, setData] = useState(null);
    const [payDlg, setPayDlg] = useState(null); // holds { quarter, target_cumulative, shortfall }
    const [amount, setAmount] = useState("");
    const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
    const [challan, setChallan] = useState("");
    const [notes, setNotes] = useState("");

    const reload = () => {
        api.get("/tax/advance-tax", { params: { fy, estimated_tax: estimatedTax } })
            .then((r) => setData(r.data));
    };
    useEffect(reload, [fy, estimatedTax]);

    const record = async () => {
        if (!amount || !payDate) { toast.error("Amount and date required"); return; }
        try {
            await api.post("/tax/advance-tax/pay", {
                financial_year: fy,
                quarter: payDlg.quarter,
                amount_paid: parseFloat(amount),
                payment_date: payDate,
                challan_no: challan || null,
                notes: notes || null,
            });
            toast.success("Payment recorded");
            setPayDlg(null); setAmount(""); setChallan(""); setNotes("");
            reload(); onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const deletePmt = async (id) => {
        if (!window.confirm("Delete this payment record?")) return;
        await api.delete(`/tax/advance-tax/${id}`);
        toast.success("Payment removed");
        reload(); onChange?.();
    };

    if (!data) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

    return (
        <div className="space-y-4" data-testid="tax-advance">
            {estimatedTax === 0 && (
                <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-xs text-stone-600 flex gap-2">
                    <AlertTriangle size={14} className="text-stone-400 mt-0.5" />
                    <span>Enter your <strong>estimated FY tax liability</strong> in the header to compute per-quarter targets. Without it, the targets show as ₹0.</span>
                </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {data.quarters.map((q) => <QuarterCard key={q.quarter} q={q} onPay={() => setPayDlg(q)} />)}
            </div>

            {data.payments.length > 0 && (
                <div className="border border-stone-200 rounded-md">
                    <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 eyebrow">Challans on record</div>
                    <table className="dense-table w-full">
                        <thead><tr><th>Quarter</th><th>Payment Date</th><th>Challan No</th><th>Notes</th><th className="text-right">Amount</th><th></th></tr></thead>
                        <tbody>
                            {data.payments.map((p) => (
                                <tr key={p.id} data-testid={`tax-payment-row-${p.id}`}>
                                    <td className="font-medium">{p.quarter}</td>
                                    <td className="tabular-nums">{fmtDate(p.payment_date)}</td>
                                    <td className="text-xs text-stone-500">{p.challan_no || "—"}</td>
                                    <td className="text-xs text-stone-500">{p.notes || "—"}</td>
                                    <td className="text-right tabular-nums font-medium">{fmtMoney(p.amount_paid)}</td>
                                    <td>
                                        <RowActions
                                            onDelete={() => deletePmt(p.id)}
                                            testIdBase="tax-payment"
                                            id={p.id}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={!!payDlg} onOpenChange={(v) => !v && setPayDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Record {payDlg?.quarter} advance tax payment</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-stone-500">
                            Target cumulative for {payDlg?.quarter}: <span className="font-mono-figure text-stone-800">{fmtMoney(payDlg?.target_cumulative || 0)}</span>
                            {payDlg?.shortfall > 0 && <> · Shortfall <span className="text-red-700 font-mono-figure">{fmtMoney(payDlg?.shortfall)}</span></>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 tabular-nums" data-testid="tax-pay-amount" /></div>
                            <div><Label>Date</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1" data-testid="tax-pay-date" /></div>
                        </div>
                        <div><Label>Challan number (optional)</Label><Input value={challan} onChange={(e) => setChallan(e.target.value)} className="mt-1" data-testid="tax-pay-challan" /></div>
                        <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 min-h-[60px]" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPayDlg(null)}>Cancel</Button>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={record} data-testid="tax-pay-save">Record payment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function QuarterCard({ q, onPay }) {
    const status = q.is_overdue ? "overdue" : q.days_to_due < 0 ? "past" : q.days_to_due <= 15 ? "due-soon" : "upcoming";
    const cfg = {
        overdue:   { bg: "border-red-300 bg-red-50",       icon: <AlertTriangle size={14} className="text-red-700" />, label: "OVERDUE" },
        "due-soon":{ bg: "border-amber-300 bg-amber-50",   icon: <Clock size={14} className="text-amber-700" />,        label: `IN ${q.days_to_due} DAYS` },
        past:      { bg: "border-stone-200 bg-white",      icon: <CheckCircle2 size={14} className="text-stone-400" />, label: "PASSED" },
        upcoming:  { bg: "border-stone-200 bg-white",      icon: <Clock size={14} className="text-stone-400" />,        label: `IN ${q.days_to_due} DAYS` },
    }[status];
    return (
        <div className={`rounded-md border p-3 ${cfg.bg}`} data-testid={`tax-quarter-${q.quarter}`}>
            <div className="flex items-center justify-between">
                <div className="font-medium">{q.quarter}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">{cfg.icon}{cfg.label}</div>
            </div>
            <div className="text-xs text-stone-500 mt-1">Due {fmtDate(q.due_date)} · Cumulative {q.cumulative_pct}%</div>
            <div className="mt-3">
                <div className="eyebrow">Target</div>
                <div className="tabular-nums font-medium">{fmtMoney(q.target_cumulative)}</div>
                <div className="eyebrow mt-2">Paid (cum.)</div>
                <div className="tabular-nums font-medium">{fmtMoney(q.paid_cumulative)}</div>
                {q.shortfall > 0 && (
                    <div className="mt-2 text-xs text-red-700">
                        Shortfall <span className="font-mono-figure">{fmtMoney(q.shortfall)}</span>
                    </div>
                )}
            </div>
            <Button size="sm" variant="outline" className="mt-3 w-full" onClick={onPay} data-testid={`tax-quarter-pay-${q.quarter}`}>
                <IndianRupee size={12} className="mr-1" /> Record payment
            </Button>
        </div>
    );
}

/* ================================================================ */
/* CAPITAL GAINS                                                     */
/* ================================================================ */
function CapitalGainsTab({ fy }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/tax/capital-gains", { params: { fy } }).then((r) => setData(r.data));
    }, [fy]);
    if (!data) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

    const hasRows = data.stcg.count > 0 || data.ltcg.count > 0;
    return (
        <div className="space-y-4" data-testid="tax-capital">
            <div className="grid gap-3 grid-cols-3">
                <Kpi label="STCG" value={fmtMoney(data.stcg.total)} sub={`${data.stcg.count} transaction${data.stcg.count === 1 ? "" : "s"}`} testid="tax-cap-stcg" />
                <Kpi label="LTCG" value={fmtMoney(data.ltcg.total)} sub={`${data.ltcg.count} transaction${data.ltcg.count === 1 ? "" : "s"}`} testid="tax-cap-ltcg" />
                <Kpi label="Net gain" value={fmtMoney(data.net_gain)} sub="Combined FY total" testid="tax-cap-net" />
            </div>

            {!hasRows && (
                <div className="border border-dashed border-stone-300 rounded-md p-6 text-center text-sm text-stone-500" data-testid="tax-cap-empty">
                    No sold investments in FY {fy}. Capital gains here are auto-derived from the <strong>Investments</strong> module —
                    when you mark an investment as sold (with `sold_date` and `sold_price`), it appears here classified as STCG / LTCG based on the holding period.
                </div>
            )}

            {data.stcg.count > 0 && <GainsTable title="STCG · Short-term capital gains" rows={data.stcg.rows} />}
            {data.ltcg.count > 0 && <GainsTable title="LTCG · Long-term capital gains" rows={data.ltcg.rows} />}
        </div>
    );
}

function GainsTable({ title, rows }) {
    return (
        <div className="border border-stone-200 rounded-md">
            <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 eyebrow">{title}</div>
            <table className="dense-table w-full">
                <thead>
                    <tr>
                        <th>Name</th><th>Kind</th>
                        <th>Bought</th><th>Sold</th>
                        <th className="text-right">Invested</th>
                        <th className="text-right">Sold</th>
                        <th className="text-right">Gain</th>
                        <th className="text-right">Holding</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`tax-cap-row-${r.id}`}>
                            <td>{r.name}</td>
                            <td className="text-xs text-stone-500">{r.kind}</td>
                            <td className="tabular-nums">{fmtDate(r.purchase_date)}</td>
                            <td className="tabular-nums">{fmtDate(r.sold_date)}</td>
                            <td className="text-right tabular-nums">{fmtMoney(r.invested_amount)}</td>
                            <td className="text-right tabular-nums">{fmtMoney(r.sold_amount)}</td>
                            <td className={`text-right tabular-nums font-medium ${r.gain >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {r.gain >= 0 ? "+ " : "− "}{fmtMoney(Math.abs(r.gain))}
                            </td>
                            <td className="text-right tabular-nums text-xs text-stone-500">{r.holding_months}m</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ================================================================ */
/* YEAR-END PACK                                                     */
/* ================================================================ */
function YearEndPackTab({ fy, estimatedTax }) {
    const [pack, setPack] = useState(null);
    useEffect(() => {
        api.get("/tax/year-end-pack", { params: { fy, estimated_tax: estimatedTax } })
            .then((r) => setPack(r.data));
    }, [fy, estimatedTax]);
    if (!pack) return <div className="p-6 text-stone-500 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Building pack…</div>;

    const dash = pack.dashboard;
    return (
        <div className="space-y-4" data-testid="tax-pack">
            <div className="flex items-center justify-between no-print">
                <div className="text-xs text-stone-500">
                    Generated {fmtDate(pack.generated_at)} by {pack.generated_by} · FY {pack.financial_year}
                </div>
                <PrintPageButton label="Print / PDF" />
            </div>

            <div className="border border-stone-200 rounded-md p-4 bg-white">
                <div className="border-b border-stone-200 pb-3 mb-3">
                    <div className="eyebrow">Year-end tax pack · FY {pack.financial_year}</div>
                    <h2 className="text-2xl font-medium mt-1">Summary of deductions, advance tax, and capital gains</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <PackStat label="Deductions" value={fmtMoney(dash.total_deductions_claimed)} sub={`${dash.overall_deduction_utilisation_pct}% of ${fmtMoney(dash.total_deduction_limit)}`} />
                    <PackStat label="Advance tax paid" value={fmtMoney(dash.advance_tax_total_paid)} sub={`Est. FY tax ${fmtMoney(estimatedTax)}`} />
                    <PackStat label="STCG" value={fmtMoney(dash.stcg_total)} />
                    <PackStat label="LTCG" value={fmtMoney(dash.ltcg_total)} />
                </div>

                <PackTable
                    title="Section-wise deductions"
                    headers={["Section", "Claimed", "Limit", "Utilisation"]}
                    rows={pack.deductions.sections.filter((s) => s.total > 0).map((s) => [
                        <span key="sec" className="font-medium">{s.section}</span>,
                        fmtMoney(s.total),
                        s.limit ? fmtMoney(s.limit) : "—",
                        s.limit ? `${s.utilisation_pct}%` : "—",
                    ])}
                />

                <PackTable
                    title="Advance tax challans"
                    headers={["Quarter", "Payment Date", "Challan", "Amount"]}
                    rows={pack.advance_tax.payments.map((p) => [
                        p.quarter, fmtDate(p.payment_date), p.challan_no || "—", fmtMoney(p.amount_paid),
                    ])}
                />

                <PackTable
                    title="Capital gains (STCG + LTCG)"
                    headers={["Name", "Kind", "Sold", "Class", "Gain"]}
                    rows={[...pack.capital_gains.stcg.rows, ...pack.capital_gains.ltcg.rows].map((r) => [
                        r.name, r.kind, fmtDate(r.sold_date), r.classification,
                        <span key="gain" className={r.gain >= 0 ? "text-emerald-700" : "text-red-700"}>{r.gain >= 0 ? "+" : "−"} {fmtMoney(Math.abs(r.gain))}</span>,
                    ])}
                />
            </div>
        </div>
    );
}

function PackStat({ label, value, sub }) {
    return (
        <div className="border border-stone-200 rounded p-3">
            <div className="eyebrow">{label}</div>
            <div className="text-xl tabular-nums font-medium mt-1">{value}</div>
            {sub && <div className="text-[11px] text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

function PackTable({ title, headers, rows }) {
    if (rows.length === 0) return null;
    return (
        <div className="mt-5">
            <div className="eyebrow mb-1">{title}</div>
            <table className="dense-table w-full">
                <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>{r.map((c, j) => <td key={j} className={j === r.length - 1 ? "text-right tabular-nums" : ""}>{c}</td>)}</tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
